import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { CatalogProgress } from './CatalogProgress';
import { getUnviewedPrizes } from '../utils/viewedPrizes';
import type { UserNFT, NFTStats, ClaimedPrize } from '../types';
import { penkiPerDayForNft } from '../utils/penki';
import { PenkiBadge, PenkiChip } from './PenkiBadge';
import { PenkiClaimCard } from './PenkiClaimCard';
import './MyNftsPage.css';

const ITEMS_PER_PAGE = 6;

export const MyNftsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<NFTStats | null>(null);
  const [activeNfts, setActiveNfts] = useState<UserNFT[]>([]);
  const [soldNfts, setSoldNfts] = useState<UserNFT[]>([]);
  const [soldTotalValue, setSoldTotalValue] = useState(0);
  const [displayedActive, setDisplayedActive] = useState(ITEMS_PER_PAGE);
  const [displayedSold, setDisplayedSold] = useState(ITEMS_PER_PAGE);
  const [showClosedClubTooltip, setShowClosedClubTooltip] = useState(false);
  const closedClubBlockRef = useRef<HTMLDivElement>(null);
  const [newPrize, setNewPrize] = useState<ClaimedPrize | null>(null);
  const [bannerFading, setBannerFading] = useState(false);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [myNftsData, soldNftsData, claimedResult] = await Promise.all([
          apiClient.getMyNfts(),
          apiClient.getMySoldNfts(),
          apiClient.getClaimedPrizes(),
        ]);
        
        setStats(myNftsData.stats);
        setActiveNfts(myNftsData.nfts);
        setSoldNfts(soldNftsData.nfts);
        setSoldTotalValue(soldNftsData.total_value);

        const unviewed = getUnviewedPrizes(claimedResult.data);
        if (unviewed.length > 0) {
          setNewPrize(unviewed[0]);
        }
      } catch (error) {
        console.error('Failed to load NFTs:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (!newPrize) return;
    const fadeTimer = setTimeout(() => setBannerFading(true), 5000);
    const removeTimer = setTimeout(() => setNewPrize(null), 5500);
    return () => { clearTimeout(fadeTimer); clearTimeout(removeTimer); };
  }, [newPrize]);

  const handleNftClick = useCallback((nft: UserNFT) => {
    navigate(`/nft/${nft.nft_id}`);
  }, [navigate]);

  const handleShowMoreActive = useCallback(() => {
    setDisplayedActive(prev => prev + ITEMS_PER_PAGE);
  }, []);

  const handleShowMoreSold = useCallback(() => {
    setDisplayedSold(prev => prev + ITEMS_PER_PAGE);
  }, []);

  const formatPrice = (price: number): string => {
    return price.toLocaleString('ru-RU') + ' ₽';
  };

  const renderNftBadges = (nft: UserNFT) => {
    if (!nft.is_granite && !nft.is_sbp_frozen && !nft.is_purchase_locked) return null;
    return (
      <div className="nft-badges">
        {nft.is_purchase_locked && (
          <span className="nft-badge badge-purchase-locked">Идёт покупка</span>
        )}
        {nft.is_sbp_frozen && (
          <span className="nft-badge badge-sbp-frozen">Заморожено</span>
        )}
        {nft.is_granite && <span className="nft-badge badge-granite">Гранитная</span>}
      </div>
    );
  };

  const renderNftCard = (nft: UserNFT) => (
    <div
      key={nft.id}
      className={`my-nft-card ${nft.status === 'sold' ? 'sold' : ''}`}
      onClick={() => handleNftClick(nft)}
    >
      {nft.has_active_prize_drop && (
        <img src="/prizebadge.png" alt="Призовая" className="nft-prize-badge" />
      )}
      <div className="my-nft-image-container">
        <img 
          src={nft.preview_image_url ?? nft.image_url} 
          alt={nft.title}
          className="my-nft-image"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/placeholder-nft.png';
          }}
        />
        {renderNftBadges(nft)}
        {nft.status !== 'sold' && (
          <PenkiChip className="nft-card-penki" value={penkiPerDayForNft(nft)} compact />
        )}
      </div>
      <div className="my-nft-info">
        <p className="my-nft-price">
          {(nft.status === 'sold' ? (nft.sale_price ?? nft.current_price) : nft.current_price).toLocaleString('ru-RU')} ₽
        </p>
        <h3 className="my-nft-title">{nft.title}</h3>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="my-nfts-page">
        <div className="my-nfts-loading">
          <div className="my-nfts-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-nfts-page">
      <PenkiBadge />

      {/* Progress Section */}
      <CatalogProgress />

      {/* Closed Club Block */}
      <div
        ref={closedClubBlockRef}
        className="closed-club-block closed-club-block--clickable"
        onClick={() => setShowClosedClubTooltip(true)}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && setShowClosedClubTooltip(true)}
        aria-label="Закрытый клуб — как вступить"
      >
        <div className="closed-club-content">
          <div className="closed-club-info-icon" aria-hidden>
            <img src="/icon-info.png" alt="" />
          </div>
          <h3 className="closed-club-title">Закрытый клуб</h3>
          <p className="closed-club-subtitle">Вход только с гранитным ху#ком!</p>
        </div>
        <div className="closed-club-image-wrap">
          <img src="/closedclub.png" alt="" className="closed-club-image" />
        </div>
      </div>

      {/* Closed Club Tooltip */}
      {showClosedClubTooltip && (() => {
        const r = closedClubBlockRef.current?.getBoundingClientRect();
        const tooltipWidth = Math.min(400, window.innerWidth - 32);
        const tooltipLeft = r ? (window.innerWidth - tooltipWidth) / 2 : 0;
        const tooltipTop = r ? r.bottom + 10 : 0;
        const arrowLeft = r ? (r.left + r.width / 2) - tooltipLeft : 0;
        return createPortal(
          <div
            className="closed-club-tooltip-overlay"
            onPointerDown={e => {
              if (e.target !== e.currentTarget) return;
              e.preventDefault();
              setShowClosedClubTooltip(false);
            }}
          >
            <div
              className="closed-club-tooltip"
              style={r ? { top: tooltipTop, left: tooltipLeft, width: tooltipWidth } : {}}
              onPointerDown={e => e.stopPropagation()}
            >
              <div
                className="closed-club-tooltip-arrow"
                style={r ? { left: arrowLeft } : {}}
              />
              <p className="closed-club-tooltip-header">
                Чтобы вступить в клуб, вы должны быть обладателем гранитного ху#ка, который:
              </p>
              <ul className="closed-club-tooltip-list">
                <li>
                  <img src="/check-icon.png" alt="" className="closed-club-tooltip-check" />
                  Прошёл все этапы торговли
                </li>
                <li>
                  <img src="/check-icon.png" alt="" className="closed-club-tooltip-check" />
                  Был куплен за 500 000 ₽
                </li>
                <li>
                  <img src="/check-icon.png" alt="" className="closed-club-tooltip-check" />
                  Выведен в блокчейн
                </li>
                <li>
                  <img src="/check-icon.png" alt="" className="closed-club-tooltip-check" />
                  Не участвует в автоматической торговле
                </li>
              </ul>
            </div>
          </div>,
          document.body
        );
      })()}

      {/* Active NFTs Section */}
      <PenkiClaimCard />
      <div className="nft-section">
        <h2 className="nft-section-page-title">Мои ху#ки</h2>
        <div className="nft-section-header-block">
          <div className="nft-section-header-icon">
            <img src="/mynft.png" alt="" className="nft-section-icon-img" />
          </div>
          <div className="nft-section-header-info">
            <span className="nft-section-header-value">
              {formatPrice(stats?.active_total_value || 0)}
            </span>
            <span className="nft-section-header-subtitle">Стоимость моей ху#ни</span>
          </div>
        </div>
        
        {activeNfts.length === 0 ? (
          <div className="nft-empty-state">
            <p className="nft-empty-text">У вас пока нет активных NFT</p>
          </div>
        ) : (
          <>
            <div className="my-nft-grid">
              {activeNfts.slice(0, displayedActive).map(renderNftCard)}
            </div>
            {displayedActive < activeNfts.length && (
              <button className="show-more-btn" onClick={handleShowMoreActive}>
                Показать ещё
              </button>
            )}
          </>
        )}
      </div>

      {/* Sold NFTs Section */}
      <div className="nft-section">
        <h2 className="nft-section-page-title">Проданные ху#ки</h2>
        <div className="nft-section-header-block">
          <div className="nft-section-header-icon">
            <img src="/sold.png" alt="" className="nft-section-icon-img" />
          </div>
          <div className="nft-section-header-info">
            <span className="nft-section-header-value">
              {formatPrice(soldTotalValue)}
            </span>
            <span className="nft-section-header-subtitle">Стоимость проданной ху#ни</span>
          </div>
        </div>
        
        {soldNfts.length === 0 ? (
          <div className="nft-empty-state">
            <p className="nft-empty-text">У вас пока нет проданных NFT</p>
          </div>
        ) : (
          <>
            <div className="my-nft-grid">
              {soldNfts.slice(0, displayedSold).map(renderNftCard)}
            </div>
            {displayedSold < soldNfts.length && (
              <button className="show-more-btn" onClick={handleShowMoreSold}>
                Показать ещё
              </button>
            )}
          </>
        )}
      </div>

      {/* New prize banner above navbar */}
      {newPrize && (
        <div className={`new-prize-banner ${bannerFading ? 'fading' : ''}`}>
          <div className="new-prize-banner-content">
            <span className="new-prize-banner-icon">🎉</span>
            <div className="new-prize-banner-text">
              <span className="new-prize-banner-title">Вы получили приз!</span>
              <span className="new-prize-banner-subtitle">Таймер завершился — приз теперь ваш</span>
            </div>
          </div>
          <button
            className="new-prize-banner-link"
            onClick={() => navigate(`/prizes/${newPrize.id}`)}
          >
            Открыть <img src="/chevron-right-violet.png" alt="" className="new-prize-banner-chevron" />
          </button>
          <div className="new-prize-banner-progress" />
        </div>
      )}
    </div>
  );
};
